import React, { useState, useEffect, useMemo } from 'react';
import './ExpenseSheets.css';
import { getExpenses } from '../../api/expenses';
import { FaPlus, FaEdit, FaChevronDown, FaSearch } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const ExpenseSheets = () => {
  console.log("ExpenseSheets render");
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filteredExpenses, setFilteredExpenses] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'descending' });
  const [searchInputs, setSearchInputs] = useState({});
  const [searchVisible, setSearchVisible] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  // Expenses state from API
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sortedExpenses = useMemo(() => {
    let sortableItems = filteredExpenses ? [...filteredExpenses] : [...expenses];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'amount') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (sortConfig.key === 'date') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [expenses, filteredExpenses, sortConfig]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedExpenses.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedExpenses.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleAdd = () => {
    navigate('/expenses/new');
  };

  const handleEdit = (expense) => {
    navigate(`/expenses/edit/${expense.id}`, {
      state: {
        expense,
        from: startDate ? startDate.toISOString().slice(0, 10) : null,
        to: endDate ? endDate.toISOString().slice(0, 10) : null,
      }
    });
  };

  const requestSort = (key) => {
    let direction = 'descending';
    if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <span className="table-sort-arrows">
          <FaChevronDown className="table-sort-arrow up faded margin-bottom" />
          <FaChevronDown className="table-sort-arrow faded margin-top" />
        </span>
      );
    }
    const isAsc = sortConfig.direction === 'ascending';
    return <FaChevronDown className={`table-sort-arrow centered${isAsc ? ' up' : ''}`} />;
  };

  const handleSearchIconClick = (key) => {
    setSearchVisible((prev) => {
      const allClosed = Object.keys(prev).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {});
      return { ...allClosed, [key]: !prev[key] };
    });
  };

  const handleSearchInputChange = (key, value) => {
    setSearchInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchInputKeyDown = (key, e) => {
    if (e.key === 'Enter') handleSearch(key);
    else if (e.key === 'Escape') setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const handleSearch = (key) => {
    const value = searchInputs[key] || '';
    setFilteredExpenses(
      expenses.filter(expense =>
        String(expense[key] || '').toLowerCase().includes(value.toLowerCase())
      )
    );
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
    setCurrentPage(1);
  };

  const handleCloseSearch = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const renderSortableHeader = (key, title) => (
    <th className="expense-table-header sortable">
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span onClick={() => requestSort(key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          {title} {getSortIcon(key)}
        </span>
        <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {searchVisible[key] && (
            <div className="table-search-popover">
              <input
                type="text"
                className="table-search-input"
                value={searchInputs[key] || ''}
                autoFocus
                onChange={e => handleSearchInputChange(key, e.target.value)}
                onKeyDown={e => handleSearchInputKeyDown(key, e)}
                placeholder={`Search ${title}...`}
              />
              <div className="table-search-btn-row">
                <button className="table-search-btn" onClick={() => handleSearch(key)}>
                  <FaSearch style={{ fontSize: '16px' }} />
                </button>
                <button className="table-search-close-btn" onClick={() => handleCloseSearch(key)}>
                  <IoMdClose style={{ fontSize: '18px' }} />
                </button>
              </div>
            </div>
          )}
          <FaSearch
            className="table-search-icon"
            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '14px' }}
            onClick={() => handleSearchIconClick(key)}
          />
        </span>
      </span>
    </th>
  );

  // ✅ STEP 4 debug (right above return)
  console.log("render rows:", currentRows.length, "expenses:", expenses.length);

  return (
    <div className="expense-sheets-container">

      {/* ✅ Task #5: error-box above title */}
      {error && <div className="expense-table-error">{error}</div>}

      <div className="expense-sheets-header">
        <h1 className="expense-sheets-title">Expenses</h1>
      </div>

      <div className="filters-wrapper">
        <div className="filters-container">
          <div className="filter-controls">
            <div className="filter-group">
              <label className="filter-label">From:</label>
              <DatePicker
                selected={startDate}
                onChange={date => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText="Select start date"
                className="date-input"
              />
            </div>
            <div className="filter-group">
              <label className="filter-label" style={{ marginLeft: '24px' }}>To:</label>
              <DatePicker
                selected={endDate}
                onChange={date => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText="Select end date"
                className="date-input"
              />
            </div>

            <button
              className="apply-filter-button"
              onClick={async () => {
                console.log("SEARCH CLICKED");
                setLoading(true);
                setError(null);
                try {
                  const token = localStorage.getItem("ezts_auth");

                  const filters = {
                    orgId: 1,
                    from: startDate ? startDate.toISOString().slice(0, 10) : undefined,
                    to: endDate ? endDate.toISOString().slice(0, 10) : undefined,
                  };

                  console.log("ABOUT TO CALL getExpenses", { filters, tokenPresent: !!token });

                  const data = await getExpenses({ filters });

                  console.log("getExpenses returned", data);
                  console.log("RAW getExpenses return:", data);
                  console.log("RAW type:", typeof data, "isArray?", Array.isArray(data));
                  console.log("RAW keys:", data && typeof data === "object" ? Object.keys(data) : null);

                  const STATUS_MAP = {
                    1: "draft",
                    2: "pending",
                    3: "approved",
                    4: "rejected",
                  };

                  const normalized = (Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])).map((e) => {
                    const statusText = STATUS_MAP[e?.status] || String(e?.status ?? "");
// Project column: prefer readable label, fallback to code
const projectLabel =
  e?.project?.description ??
  e?.projectDescription ??
  e?.projectName ??
  "";

const projectCode =
  e?.project?.projectCode ??
  e?.projectCode ??
  "";


const projectText =
  projectLabel
    ? (projectCode ? `${projectLabel} (${projectCode})` : projectLabel)
    : projectCode;

// Expense Type column: show expense type *name* from response
const expenseTypeText =
  e?.expenseTypeName ??
  e?.expenseType?.expenseTypeName ??
  e?.expenseType?.name ??
  e?.expenseTypeNameText ??
  "";


                    const dateText = e?.expenseDate ?? e?.date ?? "";

                    const amountNum = Number(e?.expenseAmount ?? e?.amount ?? 0);

                    return {
                      id: e?.expenseId ?? e?.id,
                      status: statusText,
                      project: projectText,
                      expenseType: expenseTypeText,
                      amount: Number.isFinite(amountNum) ? amountNum : 0,
                      date: dateText,
                      description: e?.description ?? "",
                      _raw: e,
                    };
                  });

                  // ✅ STEP 3 debug (after normalized)
                  console.log("normalized length:", normalized.length);
                  console.log("normalized[0]:", normalized[0]);

                  console.log("FIRST EXPENSE NORMALIZED:", normalized?.[0]);
                  setExpenses(normalized);

                  setFilteredExpenses(null);
                  setCurrentPage(1);
                } catch (err) {
                  setError("Failed to load expenses.");
                } finally {
                  setLoading(false);
                }
              }}
            >
              Search
            </button>

          </div>
        </div>

        <div className="add-button-container">
          <button className="add-button" onClick={handleAdd}>
            <FaPlus />
            Add Expense
          </button>
        </div>
      </div>

      <div className="expense-table-container">
        {loading ? (
          <div className="expense-table-loading">Loading expenses...</div>
        ) : (
          <table className="expense-table">
            <thead>
              <tr>
                {renderSortableHeader('status', 'Status')}
                {renderSortableHeader('id', 'ID')}
                {renderSortableHeader('project', 'Project')}
                {renderSortableHeader('expenseType', 'Expense Type')}
                {renderSortableHeader('amount', 'Amount')}
                {renderSortableHeader('date', 'Expense Date')}
                <th className="expense-table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length === 0 ? (
                <tr>
                  <td className="expense-table-cell" colSpan={7} style={{ textAlign: 'center' }}>
                    No expenses found.
                  </td>
                </tr>
              ) : (
                currentRows.map((expense) => (
                  <tr key={expense.id}>
                    <td className="expense-table-cell">
                      {(() => {
                        const statusText = String(expense.status ?? "");
                        return (
                          <span className={`expense-status-badge ${statusText.toLowerCase()}`}>
                            {statusText ? statusText.charAt(0).toUpperCase() + statusText.slice(1) : "-"}
                          </span>
                        );
                      })()}
                    </td>

                    <td className="expense-table-cell">{expense.id}</td>
                    <td className="expense-table-cell">{expense.project || "-"}</td>
                    <td className="expense-table-cell">{expense.expenseType || "-"}</td>

                    <td className="expense-table-cell">
                      <span className="expense-amount">${Number(expense.amount || 0).toFixed(2)}</span>
                    </td>

                    <td className="expense-table-cell">{expense.date || "-"}</td>

                    <td className="expense-table-cell">
                      <button className="expense-action-button edit" onClick={() => handleEdit(expense)}>
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="pagination">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className="pagination-arrow"
          aria-label="Previous page"
        >
          &lt;
        </button>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={currentPage}
          onChange={e => {
            let page = Number(e.target.value);
            if (page < 1) page = 1;
            if (page > totalPages) page = totalPages;
            setCurrentPage(page);
          }}
          className="pagination-input"
        />
        <span className="pagination-total">of {totalPages}</span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="pagination-arrow"
          aria-label="Next page"
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default ExpenseSheets;
